import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Play, Power, PowerOff, ArrowRight, History, Zap, Package, Check, Heart, Share2, Users, Star } from "lucide-react";

const CTX_HINT = "Variables disponibles dans le modèle : {raison_sociale}, {email}, {plan}, {cycle}, {amount}, {inscription_id}, {paypal_url}, {entreprise_code}, {chef_password}";

export default function AdminAutomations() {
  const [rules, setRules] = useState([]);
  const [catalog, setCatalog] = useState({ triggers: [], actions: [] });
  const [templates, setTemplates] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }, { data: t }, { data: f }] = await Promise.all([
        api.get("/admin/automations/catalog"),
        api.get("/admin/automations"),
        api.get("/admin/automations/templates"),
        api.get("/admin/automations/marketplace/featured"),
      ]);
      setCatalog(c);
      setRules(r);
      setTemplates(t);
      setFeatured(f.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadRuns = async () => {
    try { const { data } = await api.get("/admin/automations/runs?limit=50"); setRuns(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ id: null });
    setForm({
      name: "", description: "",
      trigger: catalog.triggers[0]?.key || "inscription.approved",
      actions: [{ kind: "send_push_admin", params: { title: "PAG", body: "", url: "/admin" } }],
      active: true,
    });
  };
  const openEdit = (r) => {
    setEditing({ id: r.id });
    setForm({
      name: r.name || "", description: r.description || "",
      trigger: r.trigger,
      actions: r.actions?.length ? r.actions : [{ kind: "send_push_admin", params: {} }],
      active: r.active,
    });
  };
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      if (editing?.id) {
        await api.patch(`/admin/automations/${editing.id}`, form);
        toast.success("Automation mise à jour");
      } else {
        await api.post("/admin/automations", form);
        toast.success("Automation créée");
      }
      setEditing(null); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer "${r.name}" ?`)) return;
    await api.delete(`/admin/automations/${r.id}`); toast.success("Supprimée"); load();
  };
  const toggle = async (r) => {
    await api.patch(`/admin/automations/${r.id}`, { active: !r.active });
    toast.success(r.active ? "Désactivée" : "Activée"); load();
  };
  const test = async (r) => {
    try {
      const { data } = await api.post(`/admin/automations/${r.id}/test`);
      toast.success(`Test OK — ${data.matched} règle(s) déclenchée(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Automations" title="Moteur sans-code" sub="Configurez des règles trigger → action pour automatiser les flux : emails, push, annonces, webhooks." />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-[#495057]">
            {rules.length} règle(s) configurée(s) · {rules.filter((r) => r.active).length} active(s)
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowFeatured(!showFeatured)} data-testid="btn-toggle-featured" variant="outline" className="border-[#FFD700]">
              <Star className="h-4 w-4 mr-2 text-[#FFD700]" /> {showFeatured ? "Masquer" : "Voir"} Coup de cœur PAG
            </Button>
            <Button onClick={() => setShowTemplates(!showTemplates)} data-testid="btn-toggle-templates" variant="outline">
              <Package className="h-4 w-4 mr-2" /> {showTemplates ? "Masquer" : "Voir"} les templates ({templates.length})
            </Button>
            <Button onClick={() => { setShowRuns(!showRuns); if (!showRuns) loadRuns(); }} data-testid="btn-toggle-runs" variant="outline">
              <History className="h-4 w-4 mr-2" /> {showRuns ? "Masquer" : "Voir"} l'historique
            </Button>
            <Button onClick={openNew} data-testid="btn-new-automation" className="bg-[#002FA7] text-white">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle automation
            </Button>
          </div>
        </div>

        {showFeatured && (
          <div className="bg-white border-2 border-[#FFD700]" data-testid="featured-panel">
            <div className="p-4 border-b border-[#FFD700] flex items-center gap-2 bg-[#FFF9DB]">
              <Star className="h-5 w-5 text-[#FFD700] fill-[#FFD700]" />
              <div>
                <div className="font-display font-black tracking-tighter text-lg">Coup de cœur PAG</div>
                <div className="text-xs text-[#495057]">Top {featured.length} templates les plus likés par la communauté</div>
              </div>
            </div>
            {featured.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#868E96]">
                Aucun template communautaire encore partagé. Créez une automation puis cliquez sur "Partager" pour lancer le marketplace !
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-3 p-4">
                {featured.map((t, idx) => (
                  <div key={t.id} className="border-2 border-[#FFD700] bg-white p-4 relative" data-testid={`featured-${t.id}`}>
                    <div className="absolute -top-3 -left-2 h-8 w-8 bg-[#FFD700] flex items-center justify-center font-display font-black text-lg tracking-tighter">
                      #{idx + 1}
                    </div>
                    <div className="mt-2 flex items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold bg-[#0A0A0A] text-white px-2 py-0.5 inline-flex items-center gap-1"><Users className="h-3 w-3" />Communauté</span>
                    </div>
                    <div className="font-display font-bold text-sm mb-1">{t.name}</div>
                    <div className="text-xs text-[#495057] mb-2 line-clamp-3">{t.description}</div>
                    <div className="text-[10px] text-[#868E96] mb-3 flex items-center justify-between">
                      <span>Par <b>{t.author_code || "PAG"}</b></span>
                      <span className="inline-flex items-center gap-1 text-[#E60000]">
                        <Heart className={`h-3 w-3 ${t.liked_by_me ? "fill-current" : ""}`} />
                        <span className="font-mono font-bold">{t.likes}</span>
                        {t.install_count > 0 && <span className="ml-2 text-[#868E96]">· {t.install_count} installations</span>}
                      </span>
                    </div>
                    <Button size="sm" onClick={async () => {
                      try {
                        await api.post(`/admin/automations/from-template/${t.id}`);
                        toast.success(`Installé : "${t.name}"`);
                        load();
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }} data-testid={`featured-install-${t.id}`} className="h-8 text-xs w-full bg-[#FFD700] text-[#0A0A0A] hover:bg-[#F0C800]">
                      <Plus className="h-3 w-3 mr-1" />Installer ce coup de cœur
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showTemplates && (
          <div className="bg-white border border-[#DEE2E6]" data-testid="templates-panel">
            <div className="p-4 border-b border-[#DEE2E6] flex items-center gap-2">
              <Package className="h-4 w-4 text-[#002FA7]" />
              <div className="font-display font-bold">Pack de templates prêts à activer</div>
              <div className="text-xs text-[#868E96] ml-auto">Cliquez sur "Installer" pour ajouter une règle pré-configurée.</div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {templates.map((t) => (
                <div key={t.id} className={`border p-4 ${t.installed ? "border-[#059669] bg-emerald-50" : t.source === "community" ? "border-[#FFD700] bg-white hover:border-[#0A0A0A]" : "border-[#DEE2E6] bg-white hover:border-[#002FA7]"}`} data-testid={`tpl-${t.id}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-[#FFD700] text-[#0A0A0A] px-2 py-0.5">{t.category}</span>
                    {t.source === "community" && (
                      <span className="text-[10px] uppercase tracking-wider font-bold bg-[#0A0A0A] text-white px-2 py-0.5 inline-flex items-center gap-1">
                        <Users className="h-3 w-3" />Communauté
                      </span>
                    )}
                    {t.installed && <span className="text-[10px] uppercase tracking-wider font-bold bg-[#059669] text-white px-2 py-0.5 inline-flex items-center gap-1"><Check className="h-3 w-3" />Installé</span>}
                  </div>
                  <div className="font-display font-bold text-sm mb-1">{t.name}</div>
                  <div className="text-xs text-[#495057] mb-2 line-clamp-3">{t.description}</div>
                  {t.source === "community" && (
                    <div className="text-[10px] text-[#868E96] mb-2 flex items-center justify-between">
                      <span>Par <b>{t.author_code || "PAG admin"}</b></span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/admin/automations/marketplace/${t.id}/like`);
                            load();
                          } catch {}
                        }}
                        data-testid={`tpl-like-${t.id}`}
                        className={`inline-flex items-center gap-1 ${t.liked_by_me ? "text-[#E60000]" : "text-[#868E96]"} hover:text-[#E60000]`}
                      >
                        <Heart className={`h-3 w-3 ${t.liked_by_me ? "fill-current" : ""}`} />
                        <span className="font-mono font-bold">{t.likes || 0}</span>
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] font-mono text-[#868E96] mb-3">
                    <span>{t.trigger}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span>{t.actions.map((a) => a.kind).join(" + ")}</span>
                  </div>
                  {t.installed ? (
                    <Button size="sm" variant="outline" disabled className="h-8 text-xs w-full" data-testid={`tpl-installed-${t.id}`}><Check className="h-3 w-3 mr-1" />Déjà installé</Button>
                  ) : (
                    <Button size="sm" onClick={async () => {
                      try {
                        await api.post(`/admin/automations/from-template/${t.id}`);
                        toast.success(`Template "${t.name}" installé`);
                        load();
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }} data-testid={`tpl-install-${t.id}`} className="h-8 text-xs w-full bg-[#002FA7] text-white">
                      <Plus className="h-3 w-3 mr-1" />Installer
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {showRuns && (
          <div className="bg-white border border-[#DEE2E6]" data-testid="runs-panel">
            <div className="p-4 border-b border-[#DEE2E6] font-display font-bold">Historique des déclenchements (50 derniers)</div>
            {runs.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#868E96]">Aucun déclenchement encore.</div>
            ) : (
              <div className="divide-y divide-[#DEE2E6]">
                {runs.map((r) => (
                  <div key={r.id} className="p-3 text-xs grid grid-cols-1 lg:grid-cols-4 gap-2">
                    <div className="font-mono text-[10px]">{new Date(r.started_at).toLocaleString("fr-FR")}</div>
                    <div className="font-semibold">{r.trigger}</div>
                    <div>{r.matched_rules_count || 0} règle(s) déclenchée(s)</div>
                    <div className="text-[#868E96] text-[10px]">
                      {(r.results || []).flatMap((rr) => (rr.actions || []).map((a, i) => (
                        <div key={i}>{a.kind}: {a.ok ? "✓" : "✗"} {a.detail}</div>
                      )))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid gap-3" data-testid="rules-list">
          {rules.length === 0 ? (
            <div className="p-8 bg-white border border-[#DEE2E6] text-center text-sm text-[#868E96]">
              Aucune automation pour le moment. Cliquez sur "Nouvelle automation" pour en créer une.
            </div>
          ) : rules.map((r) => (
            <div key={r.id} className="bg-white border border-[#DEE2E6] p-5" data-testid={`rule-${r.id}`}>
              <div className="grid lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className={`h-4 w-4 ${r.active ? "text-[#059669]" : "text-[#868E96]"}`} />
                    <div className="font-display font-bold text-lg">{r.name}</div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${r.active ? "bg-[#059669] text-white" : "bg-[#868E96] text-white"}`}>
                      {r.active ? "Active" : "Off"}
                    </span>
                  </div>
                  <div className="text-xs text-[#495057] mb-1">{r.description || "—"}</div>
                  <div className="flex items-center gap-2 text-xs mt-2">
                    <span className="font-mono text-[10px] bg-[#F1F3F5] px-2 py-0.5">{r.trigger}</span>
                    <ArrowRight className="h-3 w-3 text-[#868E96]" />
                    <span className="text-[10px] text-[#495057]">
                      {(r.actions || []).map((a) => a.kind).join(" + ")}
                    </span>
                  </div>
                </div>
                <div className="lg:col-span-4 text-xs">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] mb-1">Actions</div>
                  <div className="space-y-1">
                    {(r.actions || []).map((a, i) => (
                      <div key={i} className="border border-[#DEE2E6] p-2">
                        <div className="font-semibold text-[11px]">{a.kind}</div>
                        <div className="text-[10px] text-[#868E96] truncate">
                          {Object.entries(a.params || {}).map(([k, v]) => `${k}=${String(v).slice(0, 30)}`).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-3 flex gap-1 flex-wrap justify-end">
                  <Button size="sm" onClick={() => test(r)} data-testid={`test-${r.id}`} variant="outline" className="h-8 text-xs"><Play className="h-3 w-3 mr-1" />Tester</Button>
                  <Button size="sm" onClick={() => toggle(r)} data-testid={`toggle-${r.id}`} variant="outline" className="h-8 text-xs">
                    {r.active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                    {r.active ? "Off" : "On"}
                  </Button>
                  <Button size="sm" onClick={async () => {
                    if (!window.confirm(`Partager "${r.name}" avec la communauté PAG ?`)) return;
                    try {
                      await api.post(`/admin/automations/${r.id}/share`, { category: "communautaire" });
                      toast.success("Règle partagée dans le Marketplace");
                      load();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  }} data-testid={`share-${r.id}`} variant="outline" className="h-8 text-xs" title="Partager avec la communauté PAG"><Share2 className="h-3 w-3" /></Button>
                  <Button size="sm" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} variant="outline" className="h-8 text-xs">Éditer</Button>
                  <Button size="sm" onClick={() => del(r)} data-testid={`del-${r.id}`} variant="outline" className="h-8 text-xs border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editing && form && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-dialog">
            <div className="bg-white max-w-2xl w-full my-8 p-6 border-2 border-[#0A0A0A]">
              <div className="font-display font-black text-2xl tracking-tighter mb-4">{editing.id ? "Éditer" : "Nouvelle"} automation</div>
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nom *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auto-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="auto-desc" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Déclencheur (trigger)</Label>
                  <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                    <SelectTrigger data-testid="auto-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog.triggers.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[10px] uppercase tracking-wider">Actions</Label>
                    <Button size="sm" variant="outline" onClick={() => setForm({ ...form, actions: [...form.actions, { kind: "send_push_admin", params: {} }] })} data-testid="auto-add-action">
                      <Plus className="h-3 w-3 mr-1" />Ajouter
                    </Button>
                  </div>
                  <div className="text-[10px] text-[#868E96] mb-2">{CTX_HINT}</div>
                  <div className="space-y-3">
                    {form.actions.map((a, idx) => (
                      <div key={idx} className="border border-[#DEE2E6] p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Select value={a.kind} onValueChange={(v) => {
                            const arr = [...form.actions]; arr[idx] = { kind: v, params: {} }; setForm({ ...form, actions: arr });
                          }}>
                            <SelectTrigger className="w-64" data-testid={`action-kind-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {catalog.actions.map((ac) => <SelectItem key={ac.key} value={ac.key}>{ac.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => setForm({ ...form, actions: form.actions.filter((_, i) => i !== idx) })} data-testid={`del-action-${idx}`} className="border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                        {(catalog.actions.find((c) => c.key === a.kind)?.params || []).map((pName) => (
                          <div key={pName}>
                            <Label className="text-[9px] uppercase tracking-wider">{pName}</Label>
                            {pName === "body" || pName === "payload_template" || pName === "message" ? (
                              <Textarea rows={3} value={a.params[pName] || ""} onChange={(e) => {
                                const arr = [...form.actions]; arr[idx] = { ...a, params: { ...a.params, [pName]: e.target.value } }; setForm({ ...form, actions: arr });
                              }} data-testid={`action-param-${idx}-${pName}`} />
                            ) : (
                              <Input value={a.params[pName] || ""} onChange={(e) => {
                                const arr = [...form.actions]; arr[idx] = { ...a, params: { ...a.params, [pName]: e.target.value } }; setForm({ ...form, actions: arr });
                              }} data-testid={`action-param-${idx}-${pName}`} />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="auto-active" />
                  <Label className="text-sm">Active immédiatement</Label>
                </div>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="auto-cancel">Annuler</Button>
                <Button className="bg-[#002FA7] text-white" onClick={save} data-testid="auto-save">Enregistrer</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
